import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { nguoiDungRepository } from '../../../lib/repositories/nguoi-dung.repository';
import { nhanSuNenTangRepository } from '../../../lib/repositories/nhan-su-nen-tang.repository';
import { nhatKyRepository } from '../../../lib/repositories/nhat-ky.repository';
import type { NguoiDungRutGon } from '../types';

/** Trang tối đa của danh sách người dùng cổng tenant: 5 × 100 người, đủ cho quy mô vài trăm người (ADR-0013). */
const SO_TRANG_TENANT = 5;
const MOT_TRANG = 100;
/** Số người mỗi lần tìm ở cổng nền tảng; tìm theo tên ở server nên không cần tải hết. */
const MOT_LAN_TIM = 50;

async function tatCaNguoiDungTenant(): Promise<NguoiDungRutGon[]> {
  const ra: NguoiDungRutGon[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < SO_TRANG_TENANT; i += 1) {
    const trang = await nguoiDungRepository.danhSach({ limit: MOT_TRANG, cursor });
    ra.push(...trang.items.map((u) => ({ user_id: u.user_id, display_name: u.display_name, email: u.email })));
    if (!trang.next_cursor) break;
    cursor = trang.next_cursor;
  }
  return ra;
}

const truyVanCongTenant = (enabled: boolean) => ({
  queryKey: ['nhat-ky', 'nguoi-dung', 'cong-tenant'],
  queryFn: tatCaNguoiDungTenant,
  enabled,
  staleTime: 5 * 60_000,
});

const truyVanCuaTenant = (tenantId: string | undefined, tim: string, enabled: boolean) => ({
  queryKey: ['nhat-ky', 'nguoi-dung', tenantId, tim.trim()],
  queryFn: () => nhatKyRepository.nguoiDungCuaTenant(tenantId ?? '', tim, null, MOT_LAN_TIM).then((p) => p.items),
  enabled,
  staleTime: 60_000,
});

const truyVanNhanSu = (enabled: boolean) => ({
  queryKey: ['nhat-ky', 'nhan-su-nen-tang'],
  queryFn: (): Promise<NguoiDungRutGon[]> =>
    nhanSuNenTangRepository
      .danhSach({ limit: MOT_TRANG })
      .then((p) => p.items.map((s) => ({ user_id: s.id, display_name: s.ho_ten, email: s.email }))),
  enabled,
  staleTime: 5 * 60_000,
});

/**
 * Bảng `user_id` → tên cho các cột người mà API chỉ trả mã. Cổng tenant: người của chính tenant. Cổng nền tảng: nhân sự
 * nền tảng (có trang đầu 100 người), thêm trang đầu người của tenant nếu đang xem một tenant. Dùng chung cache với ô lọc;
 * danh sách lỗi hay thiếu quyền thì bảng rỗng, nơi dùng hiện "Không rõ tên".
 */
export function useBangTenNguoi(tenantId: string | undefined, nenTang: boolean): ReadonlyMap<string, string> {
  const qTenant = useQuery(truyVanCongTenant(!nenTang));
  const qCuaTenant = useQuery(truyVanCuaTenant(tenantId, '', nenTang && Boolean(tenantId)));
  const qNhanSu = useQuery(truyVanNhanSu(nenTang));
  return useMemo(() => {
    const bang = new Map<string, string>();
    for (const u of [...(qTenant.data ?? []), ...(qCuaTenant.data ?? []), ...(qNhanSu.data ?? [])]) {
      if (u.display_name?.trim()) bang.set(u.user_id, u.display_name);
    }
    return bang;
  }, [qTenant.data, qCuaTenant.data, qNhanSu.data]);
}

/**
 * Người dùng cho ô lọc "Người dùng" của nhật ký.
 *
 * - Cổng tenant: `GET /api/v1/users` của chính tenant trong token, tải một lần
 *   rồi lọc tại chỗ.
 * - Cổng nền tảng: `GET /api/v1/tenants/{id}/users?search=` (tìm ở server) và
 *   nhóm thứ hai là nhân sự nền tảng (`GET /api/v1/platform-staff`), vì người
 *   thực hiện có thể là quản trị nền tảng thao tác trên tenant.
 *
 * `tim` đã qua debounce ở component, nên khoá truy vấn không đổi theo từng phím.
 */
export function useNguoiDungLoc(tenantId: string, nenTang: boolean, tim: string) {
  const qTenant = useQuery(truyVanCongTenant(!nenTang));
  const qNenTang = useQuery(truyVanCuaTenant(tenantId, tim, nenTang));
  const qNhanSu = useQuery(truyVanNhanSu(nenTang));

  const nguoiDung = nenTang ? qNenTang : qTenant;
  return {
    nguoiDung: nguoiDung.data ?? [],
    nhanSu: nenTang ? (qNhanSu.data ?? []) : [],
    dangTai: nguoiDung.isFetching || (nenTang && qNhanSu.isFetching),
    loi: nguoiDung.error ?? (nenTang ? qNhanSu.error : null),
  };
}
